const STORAGE_KEY = 'helios_m365_oauth_return';
const RETURN_TTL_MS = 15 * 60 * 1000;

interface ReturnIdentity {
  tenantId: string;
  userId: string;
}

/** Keep navigation local, including when browser storage has been edited. */
function safeReturnPath(value: unknown): string | null {
  if (
    typeof value !== 'string' ||
    !value.startsWith('/') ||
    value.startsWith('//') ||
    value.includes('\\') ||
    Array.from(value).some(
      (character) => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127,
    )
  )
    return null;
  const path = value.split(/[?#]/, 1)[0]!;
  if (/^\/(auth|api|company-setup)(\/|$)/.test(path) || path === '/workspace/oauth/callback')
    return null;
  return value;
}

export function rememberM365Return(
  state: string | null,
  identity: ReturnIdentity,
  returnTo?: string,
): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    const path = safeReturnPath(returnTo);
    if (!state || !path) return;
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...identity, state, path, createdAt: Date.now() }),
    );
  } catch {
    /* OAuth still works when browser storage is unavailable. */
  }
}

export function consumeM365Return(state: string, identity: ReturnIdentity): string {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
    if (!stored) return '/workspace';
    const saved = JSON.parse(stored) as Record<string, unknown>;
    if (
      saved.state !== state ||
      saved.tenantId !== identity.tenantId ||
      saved.userId !== identity.userId ||
      typeof saved.createdAt !== 'number' ||
      Date.now() - saved.createdAt > RETURN_TTL_MS ||
      saved.createdAt > Date.now()
    )
      return '/workspace';
    return safeReturnPath(saved.path) ?? '/workspace';
  } catch {
    return '/workspace';
  }
}
